import React from 'react';
import { create, InstanceProps } from 'react-modal-promise';
import { useLocation, useNavigate } from 'react-router-dom';
import { MenuOpen } from '@mui/icons-material';
import {
  AppBar,
  Box,
  CardMedia,
  Dialog,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  styled,
  Toolbar
} from '@mui/material';

import { EXACARE_LOGO_URLS } from '@/constants';
import { usePylonChat } from '@/hooks/usePylonChat';
import { MobileNotificationsBell } from '@/pages/AdminHub/Staff/Notifications/components/MobileInboxModal';

import CurrentUser from '../../CurrentUser';
import FacilitySelector from '../../FacilitySelector';
import { useGetSidebarCRMItems } from '../getSidebarCRMItems';
import HelpCenterButton from '../HelpCenterButton';
import { isCurrentPath, StyledNavButton } from '../Sidebar';

const MobileNavigationDialog: React.FC<InstanceProps<unknown>> = ({
  isOpen,
  onResolve
}) => {
  const navigate = useNavigate();
  const pathname = useLocation().pathname;
  const crmSidebarItems = useGetSidebarCRMItems();
  const { unreadMessageCount, toggleChat } = usePylonChat();

  const navigateToUrl = (url: string) => {
    onResolve();
    navigate(url);
  };

  return (
    <StyledDialog fullScreen open={isOpen}>
      <AppBar position="sticky">
        <Toolbar>
          <IconButton edge="start">
            <MenuOpen onClick={onResolve} />
          </IconButton>
          <CardMedia component="img" src={EXACARE_LOGO_URLS.horizontal} />
          <Box sx={{ ml: 'auto' }}>
            <MobileNotificationsBell />
          </Box>
        </Toolbar>
      </AppBar>
      <List dense component="nav" disablePadding>
        {crmSidebarItems.map((item, index) => {
          const { icon, text, path, showOnMobile } = item;

          if (!showOnMobile) {
            return null;
          }

          return (
            <ListItem key={index}>
              <StyledNavButton
                onClick={() => navigateToUrl(path)}
                selected={isCurrentPath(item, pathname)}
              >
                <ListItemIcon sx={{ minWidth: 'unset' }}>{icon}</ListItemIcon>
                <ListItemText
                  sx={{ paddingLeft: 2 }}
                  primaryTypographyProps={{
                    whiteSpace: 'nowrap',
                    fontSize: '14px',
                    fontWeight: isCurrentPath(item, pathname) ? 700 : 500,
                    color: '#364955'
                  }}
                  primary={text}
                />
              </StyledNavButton>
            </ListItem>
          );
        })}
        <ListItem>
          <HelpCenterButton
            expanded
            unreadMessageCount={unreadMessageCount}
            onToggleChat={toggleChat}
          />
        </ListItem>
        <ListItem>
          <FacilitySelector expanded />
        </ListItem>
        <ListItem sx={{ mb: 1 }}>
          <CurrentUser expanded handleNavigate={onResolve} />
        </ListItem>
      </List>
    </StyledDialog>
  );
};

export const showMobileNavigationDialog = create(MobileNavigationDialog);

const StyledDialog = styled(Dialog)({
  '& .MuiPaper-root': {
    boxShadow: 'unset',
    backgroundColor: '#E7EEF4',
    padding: 0
  },
  '& .MuiToolbar-root': {
    height: 64
  },
  '& .MuiCardMedia-root': {
    maxWidth: 140,
    position: 'absolute',
    margin: '0 auto',
    left: 0,
    right: 0
  },
  '& .MuiList-root': {
    display: 'flex',
    flexDirection: 'column'
  },
  '& nav': {
    height: '100%'
  }
});
